/**
 * Portal Guard — Routes users to the correct portal based on their role.
 * Prevents unauthorized access to portals that a user's role doesn't permit.
 * Supports multi-role: secondary roles (e.g. domain_creator) grant additional portal access.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export type PortalType = 'client' | 'sme' | 'em' | 'admin' | 'domain_management';

interface PortalGuardProps {
  portal: PortalType;
  userRole: string | null;
  secondaryRoles?: string[];
  children: React.ReactNode;
}

/**
 * Explicit role lists for each portal.
 * SME portal access also includes any role starting with 'sme_' (see hasPortalAccess).
 */
const PORTAL_ROLES: Record<PortalType, string[]> = {
  client: ['client', 'super_admin'],
  sme: [
    'consultant', 'super_admin',
    // Individual sme_* roles are handled dynamically in hasPortalAccess
  ],
  em: [
    'engagement_manager', 'engagement_lead', 'super_admin',
    // Individual em_* roles are handled dynamically in hasPortalAccess
  ],
  admin: ['super_admin', 'user_admin'],
  domain_management: ['engagement_manager', 'engagement_lead', 'content_manager', 'super_admin'],
};

/**
 * Determines which portal a user should land on based on their role.
 */
export function getDefaultPortal(role: string | null, secondaryRoles?: string[]): PortalType | 'pending' {
  if (!role) return 'pending';
  if (['super_admin', 'user_admin'].includes(role)) return 'admin';
  if (role === 'content_manager') return 'domain_management';
  if (['engagement_manager', 'engagement_lead'].includes(role) || role.startsWith('em_')) return 'em';
  if (['consultant'].includes(role) || role.startsWith('sme_')) return 'sme';
  if (role === 'client') return 'client';
  return (['admin', 'em', 'sme', 'domain_management', 'client'] as PortalType[])
    .find(portal => hasPortalAccess(role, portal, secondaryRoles)) || 'pending';
}

/**
 * Checks if a given role has access to a portal.
 * Also checks secondary roles for portal access (multi-role support).
 */
export function hasPortalAccess(role: string | null, portal: PortalType, secondaryRoles?: string[]): boolean {
  if (!role) return false;
  return [role, ...(secondaryRoles || [])].some(candidate =>
    PORTAL_ROLES[portal].includes(candidate)
    || (portal === 'sme' && candidate.startsWith('sme_'))
    || ((portal === 'em' || portal === 'domain_management') && candidate.startsWith('em_'))
  );
}

const PortalGuard: React.FC<PortalGuardProps> = ({ portal, userRole, secondaryRoles, children }) => {
  const { isAuthenticated, isEnterpriseUser } = useAuth();

  if (!isAuthenticated || !isEnterpriseUser) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPortalAccess(userRole, portal, secondaryRoles)) {
    const defaultPortal = getDefaultPortal(userRole, secondaryRoles);
    return <Navigate to={`/${defaultPortal}`} replace />;
  }

  return <>{children}</>;
};

export default PortalGuard;
